import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useSeo } from '@/hooks/useSeo';
import { useJsonLd } from '@/hooks/useJsonLd';
import { SITE_URL, SITE_NAME } from '@/lib/site';
import { getArticle, getArticles } from '@/content/articles';
import { MAIN_SITE_URL } from '@/mocks/categories';

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const article = getArticle(i18n.language, slug ?? 'allchinabuy-spreadsheet-guide');
  const allArticles = getArticles(i18n.language);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug, i18n.language]);

  const jsonLd = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.metaDescription,
        author: { '@type': 'Organization', name: article.author },
        publisher: { '@type': 'Organization', name: SITE_NAME },
        datePublished: article.publishedDate,
        dateModified: article.updatedDate,
        mainEntityOfPage: `${SITE_URL}/article/${article.slug}`,
      }
    : undefined;

  useSeo({
    title: article?.title ?? t('home.articleNotFound'),
    description: article?.metaDescription,
    path: article ? `/article/${article.slug}` : '/',
    type: 'article',
  });
  useJsonLd(jsonLd);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="mx-auto max-w-3xl px-4 py-20 text-center md:px-6">
          <h1 className="font-heading text-2xl font-semibold text-foreground-950">
            {t('home.articleNotFound')}
          </h1>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 cursor-pointer">
            <i className="ri-home-line"></i>
            {t('nav.home')}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = allArticles.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <article className="mx-auto max-w-3xl px-4 py-12 md:px-6 md:py-16">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-700 cursor-pointer">
              {t('nav.home')}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{article.title}</span>
          </nav>

          <h1 className="mt-6 font-heading text-2xl font-semibold leading-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.publishedDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
          </div>

          <div className="mt-8 space-y-5">
            {article.intro.map((paragraph, i) => (
              <p key={i} className="text-base leading-relaxed text-foreground-700">
                {paragraph}
              </p>
            ))}
          </div>

          {article.sections.map((section) => (
            <section key={section.heading} className="mt-10">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                {section.heading}
              </h2>
              <div className="mt-4 space-y-4">
                {section.paragraphs.map((paragraph, i) => (
                  <p key={i} className="text-base leading-relaxed text-foreground-700">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}

          <section className="mt-10">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {article.tableTitle}
            </h2>
            <div className="mt-4 overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead>
                  <tr className="bg-background-100">
                    {article.tableHeaders.map((header) => (
                      <th key={header} className="px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {article.tableRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className="border-t border-background-200">
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className={`px-4 py-3 ${
                            cellIndex === 0 ? 'font-medium text-foreground-900' : 'text-foreground-700'
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {t('home.faqTitle')}
            </h2>
            <div className="mt-4 space-y-3">
              {article.faq.map((item) => (
                <div key={item.q} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="font-heading text-base font-semibold text-foreground-900">{item.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10 rounded-lg bg-primary-100/70 p-6">
            <div className="space-y-4">
              {article.conclusion.map((paragraph, i) => (
                <p key={i} className="text-base leading-relaxed text-foreground-700">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                {t('footer.backHome')}
              </Link>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap"
              >
                {t('buttons.allchinabuySpreadsheet')}
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </section>

          {related.length > 0 && (
            <section className="mt-12">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                {t('home.relatedGuides')}
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {related.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 cursor-pointer"
                  >
                    <h3 className="font-heading text-sm font-semibold leading-snug text-foreground-900">
                      {a.title}
                    </h3>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-700">
                      {t('home.readNow')}
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
}